import { Meeting, MeetingSession, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { PlayersGrid } from "../player/PlayersGrid";
import { PlayerItem } from "../player/interfaces";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { BoxContainer, ReactIcons } from "@kalyzee/kalyzee-ui";

import { SlavePlayerWorkBox } from "./SlavePlayerWorkBox";

import workBoxStyles from "./WorkBox.module.css";
import styles from "./GridWorkBox.module.css";

export interface PlayerGridWorkBoxProps {
  meeting: Meeting;
  sessions: MeetingSession[];
  ownSession: MeetingSession;
  videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[];
  columns?: number;
  rows?: number;
  gap?: number;
  totalNumber?: number;
  preset?: keyof typeof WorkBoxPresets | "gridonly";
};

const WorkBoxPresets = {
  "default": (sessions: number) => ({
    topBarProps: {
        center: [
          <span className={workBoxStyles.label}>
            {`${sessions} Participant${sessions > 1 ? "s" : ""}`}
          </span>
        ],
      }
  }),
  "audio": (sessions: number) => ({
    className: styles.bgAudioPreset,
    topBarProps: {
        className: styles.topBarBgAudioPreset,
        center: [
          <span className={workBoxStyles.label}>
            {`${sessions} ${sessions > 1 ? "Participants ont" : "Participant a"} la parole`}
            <ReactIcons.Microphone style={{ width: "1.5em" }} />
          </span>
        ],
      }
  }),
};

export const PlayerGridWorkBox: React.FC<PlayerGridWorkBoxProps> = ({
  meeting,
  sessions,
  ownSession,
  videoSessions,
  columns,
  rows,
  gap,
  totalNumber,
  preset = "default",
}) => {
  const sessionsToPlayerData = (): PlayerItem[] => {
    return videoSessions.map((videoSession) => {
      // Use sessions prop for fresh state (videoSession.data is mutated in-place
      // by the SDK's setMediaSessions after render, causing a 1-update lag)
      const freshSession = sessions.find(s => s.id === videoSession.data.session.id) ?? videoSession.data.session;
      const freshMedia = freshSession.medias?.find(m => m.id === videoSession.data.mediaSession.id) ?? videoSession.data.mediaSession;
      return new PlayerItem({
        type: "video_session",
        videoSession,
        extra: {
          meetingSession: freshSession,
          mediaSession: freshMedia,
        }
      });
    });
  };

  const renderGrid = () => {
    const items = sessionsToPlayerData();
    return (
      <PlayersGrid
        items={items}
        style={{ width: "100%", flex: 1, minHeight: 0, padding: preset === "gridonly" ? "unset" : "1em" }}
        meeting={meeting}
        sessions={sessions}
        gridProps={{
          column: columns,
          row: rows,
          autoAdjust: true,
          gap: gap,
        }}
        noPages={true}
        renderItem={(renderDefaultPlayer, item) => {
          if (!item) return undefined;
          const itemData = item.getData();
          const session = itemData.extra?.meetingSession;
          const mediaSession = itemData.extra?.mediaSession;
          return renderDefaultPlayer((children, renderingCtx) => {
            if (!session || !mediaSession) return children;
            return (
              <SlavePlayerWorkBox
                ownSession={ownSession}
                session={session}
                mediaSession={mediaSession}
                mediaControls
                meetingOngoing={meeting?.state === MeetingState.RUNNING}
                isPlayingSound={renderingCtx?.isPlayingSound}
              >
                {children}
              </SlavePlayerWorkBox>
            );
          });
        }}
        defaultRendererProviderOptions={{
          hideSoundEffect: false,
          muted: true,
        }}
      />
    );
  };

  if (preset === "gridonly") {
    return renderGrid();
  }

  const BoxProps = WorkBoxPresets[preset](totalNumber === undefined ? videoSessions.length : totalNumber);
  
  return (
    <BoxContainer {...BoxProps}>
      {renderGrid()}
    </BoxContainer>
  )
};